import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { workspaceApi, type Workspace } from '../../api/workspaces';

const STATUS: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-700', active: 'bg-emerald-100 text-emerald-800',
  suspended: 'bg-amber-100 text-amber-800', archived: 'bg-slate-100 text-slate-600',
};

export function WorkspaceListPage() {
  const [items, setItems] = useState<Workspace[]>([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError('');
    try {
      setItems(q.trim()
        ? (await workspaceApi.search({ q: q.trim(), ...(status ? { status } : {}) })).results
        : await workspaceApi.list(status ? { status, q: q || undefined } : { q: q || undefined }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Workspaces</h1>
          <p className="mt-1 text-sm text-slate-600">Operational boundaries within your tenant.</p>
        </div>
        <Link to="/workspaces/new" className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create workspace</Link>
        <Link to="/workspaces/search" className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-800">Advanced search</Link>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); void load(); }} className="mt-6 flex flex-wrap gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="min-w-[200px] flex-1 rounded-lg border px-3 py-2" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border px-3 py-2">
          <option value="">All</option><option value="active">Active</option><option value="pending">Pending</option>
        </select>
        <button type="submit" className="rounded-lg border px-4 py-2 text-sm">Search</button>
      </form>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {loading ? <p className="mt-6">Loading…</p> : (
        <div className="mt-6 overflow-x-auto rounded-xl border bg-white">
          <table className="min-w-full text-sm">
            <thead className="border-b bg-slate-50"><tr>
              <th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Updated</th>
            </tr></thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3"><Link to={`/workspaces/${w.id}`} className="font-medium hover:underline">{w.name}</Link></td>
                  <td className="px-4 py-3 text-slate-600">{w.type || 'general'}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${STATUS[w.status]}`}>{w.status}</span></td>
                  <td className="px-4 py-3 text-slate-500">{new Date(w.updated_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
